/**
 * Widget del gruppo "Mailing e strumenti": ultima campagna, contatti,
 * azioni rapide, nota personale.
 */

import { Link } from 'react-router-dom';
import {
  Building2,
  Calendar,
  CreditCard,
  FileText,
  Inbox,
  Mail,
  Package,
  Percent,
  Receipt,
  Send,
  StickyNote,
  Target,
  TrendingUp,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type { CSSProperties } from 'react';
import { cn } from '../../../lib/cn';
import { numeroIntero } from '../../../lib/dashboard/formato';
import { collegamentiDaMostrare } from '../../../lib/dashboard/collegamenti';
import { esitoCampagna } from '../../../lib/dashboard/kpiWidget';
import { useFonte, useRicaricaFonte } from '../../../hooks/useFonte';
import { Anello, Etichetta, Fonte, NumeroGrande, Pillola, StatoVuoto } from './parti';
import type { PropsWidget } from './tipi';

export function WidgetUltimaCampagna({ taglia }: PropsWidget<'ultima-campagna'>) {
  const campagna = useFonte('ultimaCampagna');
  const ricarica = useRicaricaFonte();

  return (
    <Fonte stato={campagna} onRiprova={() => ricarica('ultimaCampagna')}>
      {(c) => {
        if (!c) {
          return <StatoVuoto icona={Send} testo="Nessuna campagna inviata" azione={taglia === 'M' ? { etichetta: 'Crea campagna', percorso: '/mailing/campagne' } : undefined} />;
        }
        const e = esitoCampagna(c);
        const pct = (n: number | null) => (n === null ? '—' : `${n}%`);
        return (
          <div className="flex h-full min-h-0 items-center gap-3">
            <Anello quota={e.aperturePct === null ? null : e.aperturePct / 100} testo={pct(e.aperturePct)} dimensione={taglia === 'S' ? 60 : 72} />
            <div className="min-w-0 flex-1">
              <p className="truncate font-display text-sm font-semibold">{c.name}</p>
              <Etichetta>aperture</Etichetta>
              {taglia === 'M' && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <Pillola>{numeroIntero(e.inviate)} inviate</Pillola>
                  <Pillola>{pct(e.clicPct)} clic</Pillola>
                </div>
              )}
            </div>
          </div>
        );
      }}
    </Fonte>
  );
}

export function WidgetContattiMailing({ taglia }: PropsWidget<'contatti-mailing'>) {
  const contatti = useFonte('contatti');
  const ricarica = useRicaricaFonte();

  return (
    <Fonte stato={contatti} onRiprova={() => ricarica('contatti')}>
      {(c) => (
        <div className="flex h-full flex-col justify-end">
          <Etichetta>ricevono le tue email</Etichetta>
          <NumeroGrande valore={numeroIntero(c.attivi)} taglia={taglia} className="mt-1" />
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Pillola>+{c.nuoviMese} questo mese</Pillola>
            {taglia === 'M' && c.inAttesa > 0 && <Pillola>{c.inAttesa} da confermare</Pillola>}
            {taglia === 'M' && c.fuori > 0 && <Pillola>{c.fuori} non raggiungibili</Pillola>}
          </div>
        </div>
      )}
    </Fonte>
  );
}

const ICONE_COLLEGAMENTI: Record<string, LucideIcon> = {
  clienti: Users,
  fatture: Receipt,
  prodotti: Package,
  offerte: FileText,
  calendario: Calendar,
  attivita: Building2,
  abbonamenti: CreditCard,
  lead: Target,
  analisi: TrendingUp,
  passivita: Inbox,
  provvigioni: Percent,
  mailing: Mail,
};

const QUANTI_COLLEGAMENTI = { S: 4, M: 4, L: 8, XL: 16 } as const;
const COLONNE_COLLEGAMENTI = { S: 'grid-cols-2', M: 'grid-cols-4', L: 'grid-cols-4', XL: 'grid-cols-4 xl:grid-cols-8' } as const;

/** Le sezioni dell'app come icone del telefono: un tocco e ci sei. */
export function WidgetAzioniRapide({ taglia, impostazioni, contesto }: PropsWidget<'azioni-rapide'>) {
  const collegamenti = collegamentiDaMostrare(contesto.ruolo, impostazioni.collegamenti).slice(0, QUANTI_COLLEGAMENTI[taglia]);
  if (collegamenti.length === 0) return <StatoVuoto icona={Building2} testo="Nessuna sezione per il tuo ruolo" />;
  const conNome = taglia !== 'S';

  return (
    <ul className={cn('grid h-full content-center justify-items-center gap-x-2 gap-y-2.5', COLONNE_COLLEGAMENTI[taglia])}>
      {collegamenti.map((c) => {
        const Icona = ICONE_COLLEGAMENTI[c.id] ?? FileText;
        return (
          <li key={c.id} className="w-full min-w-0">
            <Link to={c.href} title={c.descrizione} className="group flex flex-col items-center gap-1 outline-none">
              <span
                className={cn(
                  'relative flex items-center justify-center overflow-hidden rounded-2xl text-white shadow-[0_6px_14px_-6px_rgba(0,0,0,0.5)] transition duration-200 group-hover:-translate-y-0.5 group-hover:scale-105 group-active:scale-95 group-focus-visible:ring-4 group-focus-visible:ring-primary-200',
                  conNome ? 'h-11 w-11' : 'h-9 w-9',
                  c.colore,
                )}
              >
                <span aria-hidden className="absolute inset-0 bg-gradient-to-br from-white/40 via-white/5 to-black/10" />
                <Icona className={cn('relative', conNome ? 'h-5 w-5' : 'h-4 w-4')} />
              </span>
              {conNome && <span className="w-full truncate text-center text-[11px] font-medium text-neutral-700">{c.titolo}</span>}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/** Le righe del quaderno, disegnate con una sfumatura ripetuta. */
const RIGHE_QUADERNO: CSSProperties = {
  backgroundImage: 'repeating-linear-gradient(to bottom, transparent 0, transparent 21px, rgba(180, 83, 9, 0.14) 21px, rgba(180, 83, 9, 0.14) 22px)',
};

export function WidgetNota({ taglia, impostazioni }: PropsWidget<'nota'>) {
  if (!impostazioni.titolo && !impostazioni.testo) {
    return <StatoVuoto compatto={taglia !== 'L'} icona={StickyNote} testo="Nota vuota" sotto="La scrivi da Personalizza, nel menu ⋯" />;
  }
  return (
    <div className="flex h-full min-h-0 flex-col text-amber-950" style={RIGHE_QUADERNO}>
      {impostazioni.titolo && <p className="truncate font-display text-base font-bold leading-[22px]">{impostazioni.titolo}</p>}
      <p className={cn('min-h-0 overflow-hidden whitespace-pre-wrap break-words text-[13px] leading-[22px]', taglia === 'S' ? 'line-clamp-3' : taglia === 'M' ? 'line-clamp-3' : 'line-clamp-[11]')}>
        {impostazioni.testo}
      </p>
    </div>
  );
}
